import { AxiosRequest } from "_services/api";
import { toastErro } from "components/Toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FILTROS_SOLICITACAO_VAZIOS,
  ORDENACAO_SOLICITACAO_PADRAO,
} from "../constants/filtrosSolicitacao";
import { TipoSolicitacaoPlaca } from "../constants/tipoSolicitacaoPlaca";
import { IFiltrosSolicitacao } from "../interfaces/IFiltrosSolicitacao";
import {
  IDadosSolicitarAutorizacao,
  IResultadoCriacaoLote,
} from "../interfaces/ILoteCriado";
import { DirecaoOrdenacao, IOrdenacao } from "../interfaces/IOrdenacao";
import { IResultadoImportacaoPlanilha } from "../interfaces/IResultadoImportacaoPlanilha";
import { ISolicitacaoPlaca } from "../interfaces/ISolicitacaoPlaca";
import { exportarSolicitacoesXlsx } from "../utils/exportarSolicitacoesXlsx";
import { mensagemDeErro } from "../utils/mensagemDeErro";
import { montarParametrosSolicitacoes } from "../utils/montarParametrosSolicitacoes";

const LIMITE_PADRAO = 50;
const LIMITE_TODAS_AS_PAGINAS = -1;

export function useSolicitacoes(tipo: TipoSolicitacaoPlaca) {
  // Uma instância só: se o makeApi entrar nas dependências do efeito a cada render, a busca entra em loop.
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    []
  );
  const [filtros, setFiltros] = useState<IFiltrosSolicitacao>(
    FILTROS_SOLICITACAO_VAZIOS
  );
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(LIMITE_PADRAO);
  const [ordenacao, setOrdenacao] = useState<IOrdenacao>(
    ORDENACAO_SOLICITACAO_PADRAO
  );
  const [versao, setVersao] = useState(0);
  const [reiniciarPaginacao, setReiniciarPaginacao] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState<ISolicitacaoPlaca[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [enviandoPlanilha, setEnviandoPlanilha] = useState(false);
  const [criandoLote, setCriandoLote] = useState(false);
  const ultimaBusca = useRef(0);

  useEffect(() => {
    const busca = ++ultimaBusca.current;

    async function buscar() {
      setCarregando(true);
      try {
        const parametros = montarParametrosSolicitacoes({
          tipo,
          filtros,
          pagina,
          limite,
          ordenacao,
        });
        const { data } = await msAw.get(`/solicitacao-placa?${parametros}`);
        if (busca !== ultimaBusca.current) return;
        setSolicitacoes(data?.itens ?? []);
        setTotalRegistros(data?.meta?.total ?? 0);
      } catch (erro) {
        if (busca !== ultimaBusca.current) return;
        setSolicitacoes([]);
        setTotalRegistros(0);
        toastErro(mensagemDeErro(erro, "Erro ao buscar as solicitações"));
      } finally {
        if (busca === ultimaBusca.current) setCarregando(false);
      }
    }

    buscar();
  }, [msAw, tipo, filtros, pagina, limite, ordenacao, versao]);

  const voltarParaPrimeiraPagina = useCallback(() => {
    setPagina(1);
    setReiniciarPaginacao((valor) => !valor);
  }, []);

  const recarregar = useCallback(() => {
    setVersao((valor) => valor + 1);
    voltarParaPrimeiraPagina();
  }, [voltarParaPrimeiraPagina]);

  const aplicarFiltros = useCallback(
    (novosFiltros: IFiltrosSolicitacao) => {
      setFiltros(novosFiltros);
      recarregar();
    },
    [recarregar]
  );

  const mudarLimite = useCallback(
    (novoLimite: number) => {
      setLimite(novoLimite);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  // A tabela já volta sozinha para a página 1 quando a ordenação muda.
  const ordenar = useCallback((campo: string, direcao: DirecaoOrdenacao) => {
    setOrdenacao({ campo, direcao });
    setPagina(1);
  }, []);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const parametros = montarParametrosSolicitacoes({
        tipo,
        filtros,
        pagina: 1,
        limite: LIMITE_TODAS_AS_PAGINAS,
        ordenacao,
      });
      const { data } = await msAw.get(`/solicitacao-placa?${parametros}`);
      exportarSolicitacoesXlsx(data?.itens ?? []);
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao exportar as solicitações"));
    } finally {
      setExportando(false);
    }
  }, [msAw, tipo, filtros, ordenacao]);

  async function importarPlanilha(
    arquivo: File
  ): Promise<IResultadoImportacaoPlanilha> {
    setEnviandoPlanilha(true);
    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);
      const { data } = await msAw.post(
        `/solicitacao-placa/planilha`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      recarregar();
      return {
        sucesso: true,
        inseridos: data?.inseridos ?? 0,
        inseridosTerceiros: data?.inseridosTerceiros ?? 0,
        erros: [],
      };
    } catch (error: any) {
      if (error?.response?.status === 413) {
        return {
          sucesso: false,
          inseridos: 0,
          inseridosTerceiros: 0,
          mensagem: "Arquivo maior que 10 MB",
          erros: [],
        };
      }
      return {
        sucesso: false,
        inseridos: 0,
        inseridosTerceiros: 0,
        mensagem: mensagemDeErro(error, "Erro ao importar a planilha"),
        erros: error?.response?.data?.erros ?? [],
      };
    } finally {
      setEnviandoPlanilha(false);
    }
  }

  async function criarLote(
    idsSolicitacao: number[],
    dados: IDadosSolicitarAutorizacao
  ): Promise<IResultadoCriacaoLote | null> {
    setCriandoLote(true);
    try {
      const { data } = await msAw.post(`/solicitacao-placa/lote`, {
        idsSolicitacao,
        ...dados,
      });
      recarregar();
      return data;
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao solicitar a autorização"));
      return null;
    } finally {
      setCriandoLote(false);
    }
  }

  return {
    solicitacoes,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    recarregar,
    exportar,
    exportando,
    importarPlanilha,
    enviandoPlanilha,
    criarLote,
    criandoLote,
  };
}
